import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import { Plus, Trash2, Printer, Stethoscope, QrCode } from "lucide-react";
import QRCode from "qrcode";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { IBMPlexSansArabicRegular } from "../fonts/IBMPlexSansArabicFont";
import { TreatmentTemplateBase64 } from "../fonts/TreatmentTemplateBase64";
import ToothChart from "../components/ToothChart";

const SERVICES_LIST = [
  { value: "IMPLANT",   ar: "زراعة",          en: "IMPLANT" },
  { value: "ABUTMENT",  ar: "تركيبة زرعة",    en: "ABUTMENT/CROWN" },
  { value: "BONE_GR",   ar: "زراعة عظم",       en: "BONE GRAFT" },
  { value: "GUM_GR",    ar: "زراعة لثة",       en: "GUM GRAFT" },
  { value: "EXO",       ar: "خلع",            en: "EXO" },
  { value: "RCT",      ar: "علاج عصب",       en: "RCT" },
  { value: "PERIO",    ar: "لثة / جراحة",    en: "PERIO" },
  { value: "RESTO",    ar: "حشوات",          en: "RESTO" },
  { value: "INLAY",    ar: "حشوات عصبية",    en: "INLAY" },
  { value: "VENEER",   ar: "فينيير",         en: "VENEER" },
  { value: "CROWN",    ar: "تلبيسة",         en: "CROWN" },
  { value: "PC",       ar: "دعامة وتد",      en: "P&C" },
  { value: "RCTRIX",   ar: "إعادة عصب",      en: "RCTRIX" },
  { value: "RCT2",     ar: "عصب",            en: "RCT" },
  { value: "SINUS",    ar: "رفع جيب",        en: "SINUS LIFT" },
  { value: "PONTIC",   ar: "سن معلق",        en: "PONTIC" },
  { value: "ORTHO",    ar: "تقويم",          en: "ORTHO" },
  { value: "TEMP_CROWN", ar: "تركيبة مؤقتة", en: "TEMP CROWN" },
];

// Legacy mapping (for older saved plans that used different codes)
const LEGACY_MAP = {
  implant: "IMPLANT",
  crown: "CROWN",
  root_canal: "RCT",
  extraction: "EXO",
  sinus_lift: "SINUS",
  pontic: "PONTIC",
};

let DYNAMIC_SERVICES = [...SERVICES_LIST];

const findService = (val) => {
  const v = LEGACY_MAP[val] || val;
  return DYNAMIC_SERVICES.find(s => s.value === v) || SERVICES_LIST.find(s => s.value === v);
};

const LABELS = {
  ar: {
    title: "الخطة العلاجية",
    patientName: "اسم المراجع",
    fileNumber: "رقم الملف الطبي",
    nationalId: "رقم الهوية",
    service: "الخدمة",
    addService: "إضافة خدمة",
    toothNumbers: "أرقام الأسنان",
    toothCount: "عدد الأسنان",
    addTooth: "إضافة سن",
    totalPrice: "إجمالي السعر (ريال)",
    notes: "ملاحظات عامة",
    notesPlaceholder: "أدخل أي ملاحظات إضافية...",
    exportPDF: "طباعة",
    removeService: "حذف",
    selectService: "اختر الخدمة",
    toothNumber: "رقم السن",
    treatmentPlan: "Treatment Plan",
    patient: "Patient",
    file: "File No.",
    total: "Total",
    sar: "SAR",
    langSwitch: "English",
  },
  en: {
    title: "Treatment Plan",
    patientName: "Visitor Name",
    fileNumber: "Medical File Number",
    nationalId: "National ID",
    service: "Service",
    addService: "Add Service",
    toothNumbers: "Tooth Numbers",
    toothCount: "Teeth Count",
    addTooth: "Add Tooth",
    totalPrice: "Total Price (SAR)",
    notes: "General Notes",
    notesPlaceholder: "Enter any additional notes...",
    exportPDF: "Export PDF",
    removeService: "Remove",
    selectService: "Select Service",
    toothNumber: "Tooth #",
    treatmentPlan: "الخطة العلاجية",
    patient: "المريض",
    file: "رقم الملف",
    total: "الإجمالي",
    sar: "ريال",
    langSwitch: "العربية",
  }
};

const TreatmentPlan = () => {
  const [lang, setLang] = useState("ar");
  const [patientName, setPatientName] = useState("");
  const [fileNumber, setFileNumber] = useState("");
  const [nationalId, setNationalId] = useState("");
  const [services, setServices] = useState([]);
  const [totalPrice, setTotalPrice] = useState("");
  const [totalManuallyEdited, setTotalManuallyEdited] = useState(false);
  const [notes, setNotes] = useState("");
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [svcList, setSvcList] = useState(SERVICES_LIST);

  useEffect(() => {
    axios.get(`${API}/treatment-services`).then(r => {
      if (Array.isArray(r.data) && r.data.length) {
        DYNAMIC_SERVICES = r.data;
        setSvcList(r.data);
      }
    }).catch(() => {});
  }, []);

  const t = LABELS[lang];
  const serviceList = svcList.map(s => ({ value: s.value, label: lang === "ar" ? s.ar : s.en }));
  const isRTL = lang === "ar";

  const generateQR = async () => {
    try {
      const url = window.location.origin + "/treatment-plan";
      const dataUrl = await QRCode.toDataURL(url, { width: 300, margin: 2, color: { dark: "#655dc6" } });
      setQrDataUrl(dataUrl);
    } catch { toast.error("فشل في توليد QR"); }
  };

  // Auto-fill total when every service has a price (unless user manually edited it)
  useEffect(() => {
    if (totalManuallyEdited) return;
    if (services.length === 0) return;
    const allHavePrice = services.every(s => {
      const p = parseFloat(s.price);
      return !isNaN(p) && p > 0;
    });
    if (!allHavePrice) return;
    const sum = services.reduce((acc, s) => acc + parseFloat(s.price), 0);
    const sumStr = sum % 1 === 0 ? String(sum) : sum.toFixed(2);
    if (sumStr !== totalPrice) setTotalPrice(sumStr);
  }, [services, totalManuallyEdited, totalPrice]);

  const addService = () => {
    setServices(prev => [...prev, { type: "", teeth: [""], price: "" }]);
  };

  const removeService = (idx) => {
    setServices(prev => prev.filter((_, i) => i !== idx));
  };

  const updateServiceType = (idx, value) => {
    setServices(prev => prev.map((s, i) => i === idx ? { ...s, type: value } : s));
  };

  const updateServicePrice = (idx, value) => {
    const clean = value.replace(/[^0-9.]/g, "");
    setServices(prev => prev.map((s, i) => i === idx ? { ...s, price: clean } : s));
  };

  const addTooth = (sIdx) => {
    setServices(prev => prev.map((s, i) => i === sIdx ? { ...s, teeth: [...s.teeth, ""] } : s));
  };

  const removeTooth = (sIdx, tIdx) => {
    setServices(prev => prev.map((s, i) => i === sIdx ? { ...s, teeth: s.teeth.filter((_, j) => j !== tIdx) } : s));
  };

  const updateTooth = (sIdx, tIdx, value) => {
    setServices(prev => prev.map((s, i) => i === sIdx ? { ...s, teeth: s.teeth.map((tooth, j) => j === tIdx ? value : tooth) } : s));
  };

  const getServiceLabel = (value, targetLang) => {
    const sv = findService(value);
    if (!sv) return value;
    return targetLang === "ar" ? sv.ar : sv.en;
  };

  const getBilingualLabel = (value) => {
    const sv = findService(value);
    if (!sv) return value;
    return `${sv.ar} / ${sv.en}`;
  };

  const buildPlanPDF = () => {
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

    doc.addFileToVFS("IBMPlexSansArabic.ttf", IBMPlexSansArabicRegular);
    doc.addFont("IBMPlexSansArabic.ttf", "IBMPlex", "normal");
    doc.setFont("IBMPlex", "normal");

    const W = 210, H = 297;

    // === STEP 1: Place the template image as full-page background ===
    doc.addImage(TreatmentTemplateBase64, "JPEG", 0, 0, W, H);

    // === STEP 2: Template is now clean (blue removed from image) ===
    // === STEP 3: Write content inside the area ===
    const cx = 10;
    const cRight = 170;
    const cCenter = 90;
    const contentWidth = cRight - cx;
    let y = 38;

    // Date
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const yyyy = now.getFullYear();
    doc.setFontSize(9);
    doc.setTextColor(80, 80, 80);
    doc.text(`${dd}/${mm}/${yyyy} الرياض في`, cRight, y, { align: "right" });

    // Title
    y += 10;
    doc.setFontSize(14);
    doc.setTextColor(101, 93, 198);
    doc.text("Treatment Plan | الخطة العلاجية", cCenter, y, { align: "center" });

    // === SIDE-BY-SIDE INTRO (Arabic right column wider, English left column narrower) ===
    y += 11;
    const startY = y;
    const colGap = 5;
    // Arabic column takes 55% of contentWidth, English takes 45%
    const arColWidth = (contentWidth - colGap) * 0.55;
    const enColWidth = (contentWidth - colGap) * 0.45;
    const arColRight = cRight;                       // Arabic right edge
    const arColLeft = cRight - arColWidth;           // Arabic left edge (=split point + gap/2)
    const enColLeft = cx;                            // English left edge
    const splitX = arColLeft - colGap;               // column split (right edge of English)

    // ---- Arabic Column (right) ----
    doc.setFontSize(9.5);
    doc.setTextColor(60, 60, 60);
    const introAr = "يتشرف مركز لوسنت للأسنان بتقديم الخطة العلاجية المقترحة لكم، والتي تم إعدادها بعناية استنادًا إلى نتائج الفحص السريري والتشخيص الطبي، وفق أعلى المعايير المهنية والطبية، وذلك بهدف الوصول إلى أفضل النتائج العلاجية وتعزيز صحة وجمال الأسنان.";
    const arLineHeight = 5.3;
    const arLines = doc.splitTextToSize(introAr, arColWidth);

    let arY = startY;
    for (let i = 0; i < arLines.length; i++) {
      const line = arLines[i];
      const words = line.split(" ").filter(w => w.length > 0);
      const isLast = i === arLines.length - 1;
      const naturalWidth = doc.getTextWidth(line);
      const normalSpaceWidth = doc.getTextWidth(" ");
      // Justify non-last lines only when natural width is already reasonably wide
      const shouldJustify = !isLast && words.length > 1 && naturalWidth > arColWidth * 0.75;
      if (shouldJustify) {
        let totalWordsWidth = 0;
        for (const w of words) totalWordsWidth += doc.getTextWidth(w);
        let gapWidth = (arColWidth - totalWordsWidth) / (words.length - 1);
        const maxGap = normalSpaceWidth * 2;
        if (gapWidth > maxGap) gapWidth = maxGap;
        let xPos = arColRight;
        for (let w = 0; w < words.length; w++) {
          doc.text(words[w], xPos, arY + (i * arLineHeight), { align: "right" });
          xPos -= doc.getTextWidth(words[w]) + gapWidth;
        }
      } else {
        doc.text(line, arColRight, arY + (i * arLineHeight), { align: "right" });
      }
    }
    arY += arLines.length * arLineHeight + 1;
    doc.setFontSize(9.5);
    doc.text("مع تمنياتنا لكم بدوام الصحة والعافية.", arColRight, arY, { align: "right" });
    arY += 5.5;
    doc.setFontSize(10);
    doc.setTextColor(101, 93, 198);
    doc.text("\"مرحلة جديدة لطب الأسنان\"", arColRight, arY, { align: "right" });

    // ---- English Column (left) ----
    doc.setFontSize(8.5);
    doc.setTextColor(100, 100, 100);
    const introEn = "Lucent Dental Center is honored to present your proposed treatment plan, carefully prepared based on the clinical examination and professional diagnosis, in accordance with the highest medical and professional standards, with the aim of achieving optimal treatment outcomes and enhancing both the health and aesthetics of your smile.";
    const enLines = doc.splitTextToSize(introEn, enColWidth);
    const enLineHeight = 4.3;
    let enY = startY;
    for (let i = 0; i < enLines.length; i++) {
      doc.text(enLines[i], enColLeft, enY + (i * enLineHeight));
    }
    enY += enLines.length * enLineHeight + 1;
    doc.text("Wishing you continued health and wellness.", enColLeft, enY);
    enY += 5;
    doc.setTextColor(101, 93, 198);
    doc.setFontSize(9);
    doc.text("\"New Era of Dentistry\"", enColLeft, enY);

    // Advance y to the maximum of both columns
    y = Math.max(arY, enY) + 3;

    // Separator line
    doc.setDrawColor(101, 93, 198);
    doc.setLineWidth(0.3);
    doc.line(cx, y, cRight, y);

    // Visitor Info - left aligned
    y += 8;
    doc.setFontSize(11);
    doc.setTextColor(50, 50, 50);
    doc.text(`Visitor :  ${patientName}`, cx, y);
    y += 8;
    doc.text(`File No. :  ${fileNumber}`, cx, y);
    if (nationalId) {
      y += 8;
      doc.text(`National ID :  ${nationalId}`, cx, y);
    }

    y += 9;

    // === SERVICES TABLE (English headers, Arabic service names) ===
    if (services.length > 0) {
      const hasAnyPrice = services.some(s => s.price && parseFloat(s.price) > 0);

      const tableHead = [
        hasAnyPrice
          ? [
              { content: "Price (SAR)", styles: { halign: "center" } },
              { content: "Tooth Numbers", styles: { halign: "center" } },
              { content: "Teeth", styles: { halign: "center" } },
              { content: "Service", styles: { halign: "center" } },
              { content: "#", styles: { halign: "center" } }
            ]
          : [
              { content: "Tooth Numbers", styles: { halign: "center" } },
              { content: "Teeth", styles: { halign: "center" } },
              { content: "Service", styles: { halign: "center" } },
              { content: "#", styles: { halign: "center" } }
            ]
      ];
      const tableBody = services.map((s, i) => {
        const teethFiltered = s.teeth.filter(t => t);
        const row = [
          teethFiltered.join(" , "),
          teethFiltered.length.toString(),
          getBilingualLabel(s.type),
          (i + 1).toString()
        ];
        if (hasAnyPrice) {
          row.unshift(s.price ? `${s.price}` : "—");
        }
        return row;
      });

      autoTable(doc, {
        startY: y,
        head: tableHead,
        body: tableBody,
        styles: {
          font: "IBMPlex",
          fontSize: 7.8,
          halign: "center",
          valign: "middle",
          cellPadding: 1.1,
          minCellHeight: 4.5,
        },
        headStyles: {
          font: "IBMPlex",
          fontStyle: "normal",
          fillColor: [101, 93, 198],
          textColor: 255,
          fontSize: 8,
          cellPadding: 1.4,
        },
        alternateRowStyles: { fillColor: [248, 248, 255] },
        margin: { left: cx, right: W - cRight },
        tableWidth: cRight - cx,
      });

      y = doc.lastAutoTable.finalY + 6;
    }

    // === TOTAL PRICE ===
    if (totalPrice) {
      doc.setFillColor(101, 93, 198);
      doc.roundedRect(cx, y, contentWidth, 9, 1, 1, "F");
      doc.setFontSize(10);
      doc.setTextColor(255, 255, 255);
      doc.text(`Total / الإجمالي:  ${totalPrice}  SAR`, cCenter, y + 6.2, { align: "center" });
      y += 12;
    }

    // === NOTES ===
    if (notes) {
      doc.setFontSize(8.5);
      doc.setTextColor(101, 93, 198);
      doc.text("Notes / ملاحظات:", cRight, y, { align: "right" });
      y += 4.5;
      doc.setFontSize(7.5);
      doc.setTextColor(50, 50, 50);
      const maxWidth = cRight - cx;
      const splitNotes = doc.splitTextToSize(notes, maxWidth);
      doc.text(splitNotes, cRight, y, { align: "right" });
      y += splitNotes.length * 3.8 + 2;
    }

    // === DISCLAIMER (Arabic right, English left) - placed at fixed bottom area ===
    const disclaimerY = 215; // raised up to leave more room
    const colHalfWidth = (contentWidth - 4) / 2;
    const arLeft = cCenter + 2;       // Arabic block starts at center and extends to right
    const enLeft = cx;                // English block starts at left margin
    const enRight = cCenter - 2;
    const arRightX = cRight;          // Arabic right edge (for align right)

    // Arabic disclaimer (right column)
    doc.setFontSize(7.5);
    doc.setTextColor(101, 93, 198);
    doc.text(": إخلاء مسؤولية", arRightX, disclaimerY, { align: "right" });

    doc.setFontSize(6.5);
    doc.setTextColor(80, 80, 80);
    const arDisclaimers = [
      "تسري الخطة العلاجية لمدة 30 يوماً من تاريخ إصدارها.",
      "قد يطرأ تعديل على خطة العلاج أو تكلفتها بعد إزالة و/أو قص التيجان القديمة أو عند ظهور مؤشرات ومتطلبات علاجية إضافية أثناء مراحل العلاج، وذلك بناءً على نتائج التقييم السريري.",
      "قد تستدعي الحالة إجراء علاجات إضافية وفقاً لشدة الحالة وتقييم السن.",
    ];
    let arY2 = disclaimerY + 4;
    const arBulletWidth = doc.getTextWidth("• ");
    for (const line of arDisclaimers) {
      const wrapped = doc.splitTextToSize(line, colHalfWidth - arBulletWidth);
      for (let i = 0; i < wrapped.length; i++) {
        if (i === 0) {
          // Render bullet at the rightmost edge (start of Arabic line)
          doc.text("•", arRightX, arY2, { align: "right" });
        }
        // Render text indented by bullet width
        doc.text(wrapped[i], arRightX - arBulletWidth, arY2, { align: "right" });
        arY2 += 3.2;
      }
      arY2 += 0.5;
    }

    // English disclaimer (left column)
    doc.setFontSize(7.5);
    doc.setTextColor(101, 93, 198);
    doc.text("Disclaimer:", enLeft, disclaimerY);

    doc.setFontSize(6.5);
    doc.setTextColor(80, 80, 80);
    const enDisclaimers = [
      "• The treatment plan is valid for 30 days from the date of issuance.",
      "• The treatment plan and estimated cost may be subject to change following the removal and/or sectioning of existing crowns, or if additional clinical findings and treatment requirements arise during the course of treatment, based on further clinical evaluation.",
      "• Additional treatment procedures may be required depending on the severity of the condition and the evaluation of the affected tooth.",
    ];
    let enY2 = disclaimerY + 4;
    for (const line of enDisclaimers) {
      const wrapped = doc.splitTextToSize(line, colHalfWidth);
      for (const w of wrapped) {
        doc.text(w, enLeft, enY2);
        enY2 += 3.2;
      }
      enY2 += 0.5;
    }

    // === SIGNATURE BOX (bottom of page) ===
    const sigY = 247;
    const sigH = 14;
    doc.setDrawColor(101, 93, 198);
    doc.setLineWidth(0.4);
    doc.roundedRect(cx, sigY, contentWidth, sigH, 1.5, 1.5, "S");
    // Header label inside the box (top)
    doc.setFontSize(8.5);
    doc.setTextColor(101, 93, 198);
    doc.text("Patient Signature", cx + 3, sigY + 4);
    doc.text("توقيع المريض", cRight - 3, sigY + 4, { align: "right" });
    // Signature & date lines
    doc.setDrawColor(140, 140, 140);
    doc.setLineWidth(0.2);
    const sigLineY = sigY + sigH - 3.5;
    // Left half: Signature line
    doc.line(cx + 3, sigLineY, cx + 78, sigLineY);
    doc.setFontSize(7);
    doc.setTextColor(110, 110, 110);
    doc.text("Signature / التوقيع", cx + 40, sigLineY + 2.6, { align: "center" });
    // Right half: Date line
    doc.line(cx + 100, sigLineY, cRight - 3, sigLineY);
    doc.text("Date / التاريخ", cx + 134, sigLineY + 2.6, { align: "center" });

    const fileName = `Treatment_Plan_${patientName.replace(/\s/g, "_")}_${fileNumber || "NA"}.pdf`;
    return { doc, fileName };
  };

  const exportPDF = async () => {
    if (!patientName) { toast.error(lang === "ar" ? "يرجى إدخال اسم المريض" : "Please enter patient name"); return; }
    const { doc, fileName } = buildPlanPDF();
    // Save the plan to backend FIRST (before triggering PDF download)
    try {
      await axios.post(`${API}/treatment-plans`, {
        patient_name: patientName,
        file_number: fileNumber,
        national_id: nationalId,
        services: services.map(s => ({ type: s.type, teeth: s.teeth.filter(t => t), price: s.price || "" })),
        total_price: totalPrice,
        notes: notes,
      });
    } catch (e) {
      console.error("Failed to save treatment plan:", e);
    }
    doc.save(fileName);
    toast.success("تم تصدير الخطة العلاجية");
  };

  return (
    <div className={`min-h-screen bg-slate-50 ${isRTL ? "rtl" : "ltr"}`} dir={isRTL ? "rtl" : "ltr"}>
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/lucent-logo.png" alt="LUCENT" className="h-10" />
            <div>
              <h1 className="text-lg font-bold text-[#655dc6]">{t.title}</h1>
              <p className="text-xs text-slate-500">Lucent Dental Center</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={generateQR}
              className="gap-2 text-[#655dc6] border-[#655dc6]"
              data-testid="qr-btn"
            >
              <QrCode className="w-4 h-4" />
              QR
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* QR Code Modal */}
        {qrDataUrl && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setQrDataUrl("")}>
            <div className="bg-white rounded-xl p-6 text-center space-y-4 max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
              <h3 className="font-bold text-[#655dc6] text-lg">{lang === "ar" ? "رمز QR للخطة العلاجية" : "Treatment Plan QR Code"}</h3>
              <img src={qrDataUrl} alt="QR Code" className="mx-auto w-64 h-64" />
              <p className="text-sm text-slate-500 break-all">{window.location.origin}/treatment-plan</p>
              <div className="flex gap-2">
                <a href={qrDataUrl} download="treatment-plan-qr.png" className="flex-1">
                  <Button className="w-full bg-[#655dc6] hover:bg-[#5449a8] text-white">{lang === "ar" ? "تحميل" : "Download"}</Button>
                </a>
                <Button variant="outline" onClick={() => setQrDataUrl("")} className="flex-1">{lang === "ar" ? "إغلاق" : "Close"}</Button>
              </div>
            </div>
          </div>
        )}
        {/* Patient Info */}
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{t.patientName}</Label>
                <Input
                  value={patientName}
                  onChange={(e) => setPatientName(e.target.value)}
                  placeholder={lang === "ar" ? "أدخل اسم المريض" : "Enter patient name"}
                  data-testid="patient-name"
                />
              </div>
              <div className="space-y-2">
                <Label>{t.fileNumber}</Label>
                <Input
                  value={fileNumber}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, "");
                    setFileNumber(val);
                  }}
                  placeholder={lang === "ar" ? "رقم الملف" : "File number"}
                  inputMode="numeric"
                  dir="ltr"
                  data-testid="file-number"
                />
              </div>
              <div className="space-y-2">
                <Label>{t.nationalId}</Label>
                <Input
                  value={nationalId}
                  onChange={(e) => setNationalId(e.target.value.replace(/[^0-9]/g, "").slice(0, 10))}
                  placeholder={lang === "ar" ? "رقم الهوية (10 أرقام)" : "National ID (10 digits)"}
                  inputMode="numeric"
                  dir="ltr"
                  data-testid="national-id"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Services */}
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-[#655dc6] flex items-center gap-2">
                <Stethoscope className="w-4 h-4" />
                {t.service}
              </h3>
              <Button onClick={addService} size="sm" className="bg-[#655dc6] hover:bg-[#5449a8] text-white" data-testid="add-service-btn">
                <Plus className="w-4 h-4 ml-1" />
                {t.addService}
              </Button>
            </div>

            {services.map((service, sIdx) => (
              <div key={sIdx} className="bg-slate-50 rounded-lg p-4 space-y-3 border" data-testid={`service-${sIdx}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-[190px]">
                    <Select value={service.type} onValueChange={(v) => updateServiceType(sIdx, v)}>
                      <SelectTrigger data-testid={`service-type-${sIdx}`}>
                        <SelectValue placeholder={t.selectService} />
                      </SelectTrigger>
                      <SelectContent>
                        {serviceList.map(s => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-32 shrink-0">
                    <Input
                      value={service.price || ""}
                      onChange={(e) => updateServicePrice(sIdx, e.target.value)}
                      placeholder={lang === "ar" ? "السعر (اختياري)" : "Price (optional)"}
                      inputMode="decimal"
                      dir="ltr"
                      className="text-center"
                      data-testid={`service-price-${sIdx}`}
                    />
                  </div>
                  <span className="text-sm text-slate-500 whitespace-nowrap">{t.toothCount}: {service.teeth.filter(t => t).length}</span>
                  <Button variant="ghost" size="sm" onClick={() => removeService(sIdx)} className="text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm text-slate-600">{t.toothNumbers}</Label>
                    {service.teeth.filter(t => t).length > 0 && (
                      <span className="text-xs text-slate-400">{service.teeth.filter(t => t).length} سن</span>
                    )}
                  </div>
                  <ToothChart
                    selectedTeeth={service.teeth.filter(t => t)}
                    onToggleTooth={(toothNum) => {
                      setServices(prev => prev.map((s, i) => {
                        if (i !== sIdx) return s;
                        const current = s.teeth.filter(t => t);
                        const exists = current.includes(toothNum);
                        const next = exists ? current.filter(t => t !== toothNum) : [...current, toothNum];
                        return { ...s, teeth: next.length ? next : [""] };
                      }));
                    }}
                  />
                </div>
              </div>
            ))}

            {services.length === 0 && (
              <div className="text-center py-8 text-slate-400">
                <Stethoscope className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">{lang === "ar" ? "اضغط إضافة خدمة للبدء" : "Click Add Service to start"}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Total + Notes */}
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6 space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>{t.totalPrice}</Label>
                {(() => {
                  const sum = services.reduce((acc, s) => {
                    const p = parseFloat(s.price);
                    return acc + (isNaN(p) ? 0 : p);
                  }, 0);
                  if (sum <= 0) return null;
                  const currentTotal = parseFloat(totalPrice);
                  const isInSync = !isNaN(currentTotal) && Math.abs(currentTotal - sum) < 0.01;
                  return (
                    <button
                      type="button"
                      onClick={() => {
                        setTotalPrice(sum % 1 === 0 ? String(sum) : sum.toFixed(2));
                        setTotalManuallyEdited(false);
                      }}
                      className={`text-xs px-2 py-1 rounded-md transition-colors ${
                        isInSync
                          ? "bg-green-50 text-green-700 cursor-default"
                          : "bg-[#F5F3F9] text-[#655dc6] hover:bg-[#E6E2F5] cursor-pointer"
                      }`}
                      disabled={isInSync}
                      data-testid="auto-sum-btn"
                      title={lang === "ar" ? "احسب من أسعار الخدمات" : "Sum from service prices"}
                    >
                      {isInSync
                        ? (lang === "ar" ? `✓ مطابق للمجموع (${sum})` : `✓ Matches sum (${sum})`)
                        : (lang === "ar" ? `إجمالي الخدمات: ${sum} — اضغط للتعبئة` : `Sum: ${sum} — click to fill`)}
                    </button>
                  );
                })()}
              </div>
              <Input
                type="number"
                value={totalPrice}
                onChange={(e) => {
                  setTotalPrice(e.target.value);
                  setTotalManuallyEdited(true);
                }}
                placeholder="0"
                className="text-lg font-bold"
                dir="ltr"
                data-testid="total-price"
              />
            </div>
            <div className="space-y-2">
              <Label>{t.notes}</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t.notesPlaceholder}
                className="min-h-[80px]"
                data-testid="notes-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Print (saves plan + generates PDF) */}
        <Button
          onClick={exportPDF}
          className="w-full bg-[#655dc6] hover:bg-[#5449a8] text-white h-12 text-base gap-2"
          data-testid="print-treatment-plan-btn"
        >
          <Printer className="w-5 h-5" />
          {t.exportPDF}
        </Button>
      </div>
    </div>
  );
};

export default TreatmentPlan;
